import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
Typography,
Button,
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
Snackbar,
Alert,
Dialog,
DialogTitle,
DialogContent,
DialogActions
} from '@mui/material';

import axios from 'axios';
import apiUrl from '../config';
import DownloadIcon from '@mui/icons-material/Download';
import LayoutDashboard from "../layout/LayoutDashboard";
import "../CSS/Declarations.css";

const DeclarationsPage = () => {

const [declarations, setDeclarations] = useState([]);
const [selectedUserId, setSelectedUserId] = useState(null);
const [loadingMessage, setLoadingMessage] = useState('');
const [notification, setNotification] = useState({ open:false, message:'', severity:'success' });

const [openDialog, setOpenDialog] = useState(false);
const [declarationToDelete, setDeclarationToDelete] = useState(null);

const [selectedUserName, setSelectedUserName] = useState('');

const [openEditChoiceDialog, setOpenEditChoiceDialog] = useState(false);
const [editDeclarationData, setEditDeclarationData] = useState(null);

const navigate = useNavigate();
const location = useLocation();



/* FORMAT DATES */

const formatDates = (datesString) => {

if(!datesString) return "Aucun";

return datesString.split(/\s*\/\s*/)
.map(date=>{
const [year,month,day] = date.trim().split('-');
return `${day}/${month}/${year}`;
})
.join(", ");

};


/* NOM MOIS */

const getMonthName = (work, leave, maladie) => {

let source = work || leave || maladie;

if(!source) return "Non spécifié";

const [firstDate] = source.split(/\s*\/\s*/);
const parsed = new Date(firstDate.trim());

if(isNaN(parsed)) return "Non spécifié";

return parsed.toLocaleString("fr-FR",{month:"long"});

};

useEffect(() => {
  localStorage.setItem("activeMenu", "declarations");
}, []);

/* LOAD DATA */

useEffect(()=>{

const token = localStorage.getItem("token");

if(!token){
navigate("/login");
return;
}

const decoded = JSON.parse(atob(token.split('.')[1]));
const role = decoded.role?.toUpperCase(); // ✅ toujours en majuscules
const tokenUserId = decoded.user_id;

let userIdToUse;

if (role === "USER") {
  // Un USER voit toujours SES propres déclarations
  userIdToUse = tokenUserId;
} else {
  // ADMIN : prend l'ID du salarié sélectionné
  userIdToUse =
    location.state?.selectedUserId ||       // ✅ bonne clé
    localStorage.getItem("selectedUserId");

  if (!userIdToUse) {
    console.error("Aucun salarié sélectionné");
    navigate("/ListeDeclarations"); // ✅ redirige si aucun salarié
    return;
  }
}

const fetchAll = async () => {
  try {
    setLoadingMessage("Chargement des déclarations...");

    // 1) Toujours charger les déclarations
    const declarationsRes = await axios.get(
      `${apiUrl}/declarations/${userIdToUse}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    setDeclarations(declarationsRes.data);

    // 2) Seulement ADMIN charge la liste users
    if (role === "ADMIN") {
      const usersRes = await axios.get(
        `${apiUrl}/users-declarations`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const users = usersRes.data;

      const selectedUser = users.find(
        u => u.id === parseInt(userIdToUse)
      );

      if (selectedUser) {
        setSelectedUserName(`${selectedUser.nom} ${selectedUser.prenom}`);
      } else {
        setSelectedUserName("");
      }
    } else {
      // USER : pas besoin d’appeler /users-declarations
      setSelectedUserName("");
    }

  } catch (error) {
    console.error(error);

    setNotification({
      open: true,
      message: error.response?.data?.message || "Erreur lors du chargement",
      severity: "error"
    });
  }

  setLoadingMessage("");
};

setSelectedUserId(userIdToUse);

fetchAll();

},[navigate]);



/* DELETE */

const handleDelete = (id)=>{

if(!id || id === 0){
console.error("ID invalide :", id);
return;
}

setDeclarationToDelete(id);
setOpenDialog(true);

};

const handleConfirmDelete = async () => {

if(!declarationToDelete || declarationToDelete <= 0){
setNotification({
open:true,
message:"ID de déclaration invalide",
severity:"error"
});
return;
}

setOpenDialog(false);

try{

const token = localStorage.getItem("token");

await axios.delete(
`${apiUrl}/declarations/${declarationToDelete}`,
{
headers:{ Authorization:`Bearer ${token}` }
}
);

setDeclarations(prev=>
prev.filter(d=>d.Id_Declaration !== declarationToDelete)
);

setNotification({
open:true,
message:"Déclaration supprimée",
severity:"success"
});

}catch(error){

setNotification({
open:true,
message:"Erreur suppression",
severity:"error"
});

}

};



/* NAVIGATION */

const handleAddDeclaration = ()=>{
navigate('/Ajouterdeclarations',{ state:{ selectedUserId }});
};



const handleEdit = (id,travail,conge,maladie)=>{

setEditDeclarationData({
declarationId:id,
joursTravail:travail,
joursConge:conge,
joursMaladie:maladie
});

setOpenEditChoiceDialog(true);

};



return(

<LayoutDashboard>

<div className="declarations-page">

{/* HEADER */}

<div className="header">

<Typography variant="h1">
Déclarations des jours de travail
</Typography>

</div>



{/* ACTIONS */}

<div className="declarations-actions">

<Button
className="primary-btn"
onClick={handleAddDeclaration}
>

+ Ajouter déclaration

</Button>


<Button
className="primary-btn"
onClick={()=>navigate('/AjouterMaladie',{ state:{ selectedUserId } })}
>

+ Arrêt maladie

</Button>

</div>



{/* TABLE */}

<div className="declarations-table">

{loadingMessage && (
<div className="loading">
{loadingMessage}
</div>
)}


<TableContainer sx={{ overflowX: "auto" }}>

<Table>

<TableHead>

{selectedUserName && (

<TableRow>

<TableCell colSpan={8} className="table-title">

Déclarations de {selectedUserName}

</TableCell>

</TableRow>

)}


<TableRow>

<TableCell>Mois</TableCell>
<TableCell>Date</TableCell>
<TableCell>Travail</TableCell>
<TableCell>Congé</TableCell>
<TableCell>Maladie</TableCell>
<TableCell>PDF</TableCell>
<TableCell>Supprimer</TableCell>
<TableCell>Modifier</TableCell>

</TableRow>

</TableHead>


<TableBody>

{declarations.map((declaration)=>(

<TableRow key={declaration.Id_Declaration}>

<TableCell>
{getMonthName(
declaration.Jours_Selectionnes,
declaration.jours_congé,
declaration.jours_maladie
)}
</TableCell>

<TableCell>
{formatDates(declaration.Date)}
</TableCell>

<TableCell>
{declaration.Somme_Jours || "0"}
</TableCell>

<TableCell>
{declaration.Total_Jours_Congé || "0"}
</TableCell>

<TableCell>
{declaration.Total_Jours_Maladie || "0"}
</TableCell>

<TableCell align="center">

{declaration.url_document_maladie ?

<a
href={`${apiUrl}/maladies/download/${declaration.url_document_maladie?.split(/\s*\/\s*/).pop()}`}
target="_blank"
rel="noopener noreferrer"
>

<DownloadIcon/>

</a>

: "—"}

</TableCell>


<TableCell>

<Button
className="delete-btn"
onClick={()=>{

if(!declaration.Id_Declaration){
console.error("Déclaration sans ID");
return;
}

handleDelete(declaration.Id_Declaration);

}}
>

Supprimer

</Button>

</TableCell>


<TableCell>

<Button
className="primary-btn"
onClick={() =>
handleEdit(
declaration.Id_Declaration,
declaration.Jours_Selectionnes,
declaration.jours_congé,
declaration.jours_maladie
)
}
>

Modifier

</Button>

</TableCell>

</TableRow>

))}

</TableBody>

</Table>

</TableContainer>

</div>



{/* DIALOG DELETE */}

<Dialog open={openDialog} onClose={()=>setOpenDialog(false)}>

<DialogTitle>Confirmation</DialogTitle>

<DialogContent>
Supprimer cette déclaration ?
</DialogContent>

<DialogActions>

<Button onClick={()=>setOpenDialog(false)}>
Annuler
</Button>

<Button onClick={handleConfirmDelete}>
Confirmer
</Button>

</DialogActions>

</Dialog>

<Dialog
open={openEditChoiceDialog}
onClose={() => setOpenEditChoiceDialog(false)}
>

<DialogTitle>
Modifier
</DialogTitle>

<DialogContent>

<Typography>
Que souhaitez-vous modifier ?
</Typography>

</DialogContent>

<DialogActions>

<Button
onClick={() => {

navigate('/Ajouterdeclarations',{
state:{
declarationId:editDeclarationData.declarationId,
selectedUserId,
joursTravail:editDeclarationData.joursTravail,
joursConge:editDeclarationData.joursConge,
joursMaladie:editDeclarationData.joursMaladie
}
});

setOpenEditChoiceDialog(false);

}}
>

Modifier Déclaration

</Button>


<Button
onClick={() => {

const maladieBrute = editDeclarationData.joursMaladie;

if(!maladieBrute || maladieBrute.trim() === "0"){

setNotification({
open:true,
message:"Aucun arrêt maladie trouvé pour cette déclaration.",
severity:"warning"
});

setOpenEditChoiceDialog(false);
return;

}

const maladieArray = maladieBrute
.split(/\s*\/\s*/)
.map(d=>d.trim())
.filter(Boolean);

let selectedMonth=null;
let selectedYear=null;

if(maladieArray.length > 0){

const firstDate = new Date(maladieArray[0]);

selectedMonth = firstDate.getMonth() + 1;
selectedYear = firstDate.getFullYear(); // ✅ correction

}

navigate('/AjouterMaladie',{
state:{
declarationId:editDeclarationData.declarationId,
selectedUserId,
selectedMonth,
selectedYear
}
});

setOpenEditChoiceDialog(false);

}}
>

Modifier Arrêt Maladie

</Button>


<Button
onClick={()=>setOpenEditChoiceDialog(false)}
>

Annuler

</Button>

</DialogActions>

</Dialog>

{/* SNACKBAR */}

<Snackbar
open={notification.open}
autoHideDuration={3000}
onClose={()=>setNotification({...notification,open:false})}
anchorOrigin={{vertical:'top',horizontal:'center'}}
>

<Alert severity={notification.severity}>

{notification.message}

</Alert>

</Snackbar>


</div>

</LayoutDashboard>

);

};

export default DeclarationsPage;